import { Button } from '@/components/ui';
import { LABEL_CLASS } from './caseFormat';

const TEXTAREA_CLASS =
  'block w-full rounded-xl border border-gray-200 bg-white/95 px-3.5 py-2.5 text-sm text-gray-900 shadow-[0_1px_2px_rgba(15,23,42,0.05)] placeholder:text-gray-400 focus:border-primary/40 focus:outline-none focus:ring-4 focus:ring-primary/10 dark:border-gray-700 dark:bg-gray-800/90 dark:text-gray-100 dark:placeholder:text-gray-500';

export function TextArea({
  id,
  label,
  value,
  onChange,
  required,
  hint,
  rows = 3,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  hint?: string;
  rows?: number;
}): React.ReactElement {
  return (
    <div>
      <label htmlFor={id} className={LABEL_CLASS}>
        {label}
        {required && <span className="text-danger"> *</span>}
      </label>
      <textarea
        id={id}
        rows={rows}
        value={value}
        aria-required={required || undefined}
        aria-describedby={hint ? `${id}-hint` : undefined}
        onChange={(event) => onChange(event.target.value)}
        className={TEXTAREA_CLASS}
      />
      {hint && (
        <p id={`${id}-hint`} className="mt-1.5 text-xs text-gray-600 dark:text-gray-300">
          {hint}
        </p>
      )}
    </div>
  );
}

export function FormShell({
  title,
  error,
  children,
  onCancel,
  onSubmit,
  submitLabel,
  submitVariant = 'primary',
  disabled,
  isPending,
}: {
  title: string;
  error: string | null;
  children: React.ReactNode;
  onCancel: () => void;
  onSubmit: () => void;
  submitLabel: string;
  submitVariant?: 'primary' | 'danger';
  disabled: boolean;
  isPending: boolean;
}): React.ReactElement {
  return (
    <form
      className="space-y-4 rounded-xl border border-gray-200 bg-gray-50/60 p-4 dark:border-gray-700 dark:bg-gray-800/40"
      aria-label={title}
      onSubmit={(event) => {
        event.preventDefault();
        if (!disabled && !isPending) onSubmit();
      }}
    >
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
      {children}
      {error && (
        <p className="text-sm text-danger" role="alert">
          {error}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          size="sm"
          variant={submitVariant}
          disabled={disabled || isPending}
          loading={isPending}
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
